import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import useScrollPosition from '@hooks/useScrollPosition/useScrollPosition';
import Caret from '@components/shared/images/atoms/Caret/Caret';
import Carousel, { CarouselImage, CarouselImagePlaceholder } from '@components/shared/carousels/Carousel';
import { ImageAnnotationToggle } from '@components/shared/ImageAnnotation';
import OverlayButton from '@components/shared/buttons/OverlayButton/OverlayButton';
import {
  FLOOR_PLAN,
  ROOM_PHOTOGRAPHY,
  THREE_D_RENDERING,
  TWO_D_RENDERING,
} from '@constants/assetTypes';
import usePrevious from '@hooks/usePrevious/usePrevious';
import ExpandedSaveButton from '@components/shared/buttons/ExpandedSaveButton/ExpandedSaveButton';
import SaveButton from '@components/shared/buttons/SaveButton/SaveButton';
import ShowAt from '@components/shared/conditional/ShowAt/ShowAt';
import HideAt from '@components/shared/conditional/HideAt/HideAt';

import styles from './AssetCarousel.module.scss';

const IMAGE_ASPECT_RATIO = 10 / 16;

AssetCarousel.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  designerBoard: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onSelected: PropTypes.func,
  readOnly: PropTypes.bool,
  saved: PropTypes.bool,
  aspectRatio: PropTypes.number,
  // eslint-disable-next-line react/forbid-prop-types
  wrapperStyles: PropTypes.object
};
AssetCarousel.defaultProps = {
  assets: [],
  onSelected: () => {},
  readOnly: false,
  saved: false,
  aspectRatio: IMAGE_ASPECT_RATIO,
};

const getHighQualityAssets = (designerBoard) => {
  return (designerBoard?.boardAssets || [])
    .filter((ba) => ba.asset &&
      (ba.asset.assetTypeId === THREE_D_RENDERING || ba.asset.assetTypeId === ROOM_PHOTOGRAPHY))
    .map((ba) => ba.asset);
};

const getLowQualityAssets = (designerBoard) => {
  return (designerBoard?.boardAssets || [])
    .filter((ba) => ba.asset &&
      (ba.asset.assetTypeId === TWO_D_RENDERING || ba.asset.assetTypeId === FLOOR_PLAN))
    .map((ba) => ba.asset);
};

export default function AssetCarousel(props) {
  const {
    designerBoard,
    onSave,
    onSelected,
    readOnly,
    saved,
    aspectRatio,
    wrapperStyles
  } = props;

  let assets = [];
  let isFullBleed = true;
  assets = getHighQualityAssets(designerBoard);

  if (assets.length === 0) {
    assets = getLowQualityAssets(designerBoard);
    isFullBleed = false;
  }

  const previousDesignerBoard = usePrevious(designerBoard);
  const hasAnnotation = assets.some(checkForAnnotations);
  const slideAssetMapping = useRef({});
  const [toggleOn, setToggleOn] = useState(true);

  const {
    carouselRef,
    scrollToBottom,
    actionsVisible,
  } = useActionGroup();

  const {
    firstImageLoaded,
    getOnError,
    getOnLoad,
  } = useFirstImageLoaded();

  const renderSlides = () => {
    return assets.map((asset, idx) => {
      slideAssetMapping.current[idx] = asset;
      const { id, annotations } = asset;

      return (
        <CarouselImage
          alt=""
          annotation={{ assetId: id, annotations, toggleOn }}
          aspectRatio={ aspectRatio }
          key={ asset.id }
          firstImage={idx === 0}
          onError={ getOnError(idx) }
          onLoad={ getOnLoad(idx) }
          url={ asset.url }
          wrapperStyles={
            wrapperStyles ||
            (!isFullBleed ? { maxWidth: '960px', margin: '0 auto' } : {})
          }
        />
      );
    });
  };

  const handleOnSelected = (index) => {
    const asset = slideAssetMapping.current[index];
    onSelected(asset);
  };

  useEffect(() => {
    if (previousDesignerBoard && designerBoard.id !== previousDesignerBoard.id) {
      onSelected(assets[0]);
    }
  }, [designerBoard]);

  return (
    <div
      className={ `${styles.Wrapper} ${isFullBleed ? styles.isFullBleed : ''} ` }
    >
      {
        (!readOnly && actionsVisible) && (
          <div className={ styles.ActionGroup }>
            <HideAt breakpoint="desktop">
              <SaveButton
                className={ styles.SaveButton }
                onClick={ onSave }
                saved={ saved }
              />
            </HideAt>
            <ShowAt breakpoint="desktop">
              <ExpandedSaveButton
                className={ styles.ExpandedSaveButton }
                onClick={ onSave }
                saved={ saved }
              />
            </ShowAt>
          </div>
        )
      }
      <div ref={ carouselRef }>
        {
          (assets && assets.length > 0) && (
            <Carousel
              className={ styles.AssetCarousel }
              onSelected={ handleOnSelected }
            >
              { renderSlides() }
            </Carousel>
          )
        }

        {
          (!assets || assets.length === 0) && (
            <CarouselImagePlaceholder
              aspectRatio={ IMAGE_ASPECT_RATIO }
              className={ styles.SingleImageSpacer }
            />
          )
        }
      </div>
      {
        (assets && assets.length > 0 && firstImageLoaded && actionsVisible) && (
          <>
            <ViewMoreButton onClick={ scrollToBottom } />
            {
              hasAnnotation && (
                <ImageAnnotationToggle
                  className={styles.AnnotationToggle}
                  toggleOn={toggleOn}
                  onToggle={(value) => setToggleOn(value)}
                  useRebrandStyling
                />
              )
            }
          </>
        )
      }
    </div>
  );
}

function ViewMoreButton(props) {
  const { onClick } = props;
  return (
    <div
      className={ styles.ViewMore }
    >
      <div className={ styles.ViewMoreLabel }>View More</div>
      <OverlayButton
        className={ styles.ViewMoreIcon }
        onClick={ onClick }
      >
        <i role="img" aria-label="View More">
          <Caret />
        </i>
      </OverlayButton>
    </div>
  );
}

function useActionGroup() {
  const [actionsVisible, setActionsVisible] = useState(true);
  const carouselRef = useRef(null);
  const scrollPosition = useScrollPosition(typeof window === 'undefined' ? {} : window, 100);

  // callback when viewMore button is clicked
  const scrollToBottom = () => {
    if (carouselRef.current) {
      setActionsVisible(false); // preemptively turn this off
      const boundingRect = carouselRef.current.getBoundingClientRect();
      const scrollToPos = boundingRect.bottom + window.scrollY;
      window.scrollTo(0, scrollToPos);
    }
  };

  // decides hide/show state of view more button
  useEffect(() => {
    if (carouselRef.current) {
      const boundingRect = carouselRef.current.getBoundingClientRect();
      const scrolledPastHero =
        (boundingRect.bottom - window.innerHeight)
        < 0;
      if (scrolledPastHero) {
        if (actionsVisible) {
          setActionsVisible(false);
        }
      } else if (!actionsVisible) {
        setActionsVisible(true);
      }
    }
  }, [scrollPosition.y]);

  return {
    carouselRef,
    scrollToBottom,
    actionsVisible,
  };
}

function useFirstImageLoaded() {
  const [firstImageLoaded, setFirstImageLoaded] = useState(false);

  const getOnLoad = (idx = 0) => {
    if (idx === 0) {
      return () => setFirstImageLoaded(true);
    }
    return undefined;
  };

  const getOnError = (idx = 0) => {
    if (idx === 0) {
      return () => setFirstImageLoaded(true);
    }
    return undefined;
  };

  return {
    firstImageLoaded,
    getOnError,
    getOnLoad,
  };
}

function checkForAnnotations(asset) {
  if (asset.annotations && asset.annotations.length > 0) {
    return true;
  }

  return false;
}
